// Thin iframe wrapper around the owner-only draft preview endpoint. The
// public /p/{slug} render only serves PUBLISHED content (404 otherwise), so
// the editor must preview the draft through this auth-gated route instead.
// Drag-and-drop reordering used to live here; now the LLM reorders via
// run_starlark. The parent bumps `refreshKey` to force a reload after
// chat-driven mutations.
//
// FT-FOCUS: the preview is also the pointing device. Clicking anything with a
// data-block-id selects it, so the user can say "make this less cheesy"
// instead of describing which item they mean. All of the selection machinery
// lives here in the parent document — the rendered page stays JS-free, and
// /p/{slug} renders byte-identically whether or not it's being edited.
import { useCallback, useEffect, useRef, useState } from "preact/hooks";
import { FocusBlock, focusLabel } from "../lib/focusBlocks";

const FOCUS_STYLE_ID = "mp-focus-style";

// Injected into the iframe document, never into the render itself.
// pointer-events:none on embeds is what makes them selectable at all: a
// cross-origin iframe swallows clicks, so the parent would never hear about a
// click on a YouTube embed. Losing embed interactivity inside the editor is
// the accepted trade — the preview is for looking at layout; the published
// page is where embeds get used.
// data-mp-preview is the @-menu's live preview: the item under the menu
// cursor, which is NOT (yet) picked. Deliberately a different treatment from
// data-mp-focused — the two can be on screen at once, and "where the menu is
// pointing" must not look like "already added".
const FOCUS_CSS = `
[data-block-id] { cursor: pointer; position: relative; }
[data-block-id]::before {
  content: "";
  display: block;
  position: absolute;
  left: -28px; top: 4px;
  width: 16px; height: 16px;
  border: 2px solid rgba(13,110,253,.3);
  border-radius: 3px;
  background: transparent;
  flex-shrink: 0;
  transition: opacity .15s, background .15s, border-color .15s;
  pointer-events: none;
  opacity: 0;
}
[data-block-id]:hover::before {
  opacity: 1;
  border-color: rgba(13,110,253,.7);
}
[data-mp-focused] { outline: 2px solid #0d6efd; outline-offset: 3px; }
[data-mp-focused]::before {
  opacity: 1;
  border-color: #0d6efd;
  background: #0d6efd;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 16 16'%3E%3Cpath fill='white' d='M12.207 4.793a1 1 0 0 1 0 1.414l-5 5a1 1 0 0 1-1.414 0l-2-2a1 1 0 0 1 1.414-1.414L6.5 9.086l4.293-4.293a1 1 0 0 1 1.414 0z'/%3E%3C/svg%3E");
  background-size: 12px 12px;
  background-position: center;
  background-repeat: no-repeat;
}
.embed, stripe-buy-button { pointer-events: none; }
[data-mp-preview] { outline: 2px dashed #fd7e14; outline-offset: 3px; background: rgba(253,126,20,.12); }
`;

// Finds the block under a point. elementFromPoint handles the normal case;
// embeds are pointer-events:none (see above) so the hit lands on their
// container instead, and only a geometric test can attribute it. Block counts
// are tiny, so the linear scan is cheaper than maintaining overlay shims.
function blockAt(doc: Document, x: number, y: number): Element | null {
  const hit = doc.elementFromPoint(x, y)?.closest("[data-block-id]");
  if (hit) return hit;
  for (const el of Array.from(doc.querySelectorAll("[data-block-id]"))) {
    const r = el.getBoundingClientRect();
    if (x >= r.left && x <= r.right && y >= r.top && y <= r.bottom) return el;
  }
  return null;
}

function toFocusBlock(el: Element): FocusBlock {
  const kind = el.getAttribute("data-block-kind") ?? "";
  return {
    id: el.getAttribute("data-block-id") ?? "",
    kind,
    label: focusLabel(el, kind),
  };
}

export function PortalPreview({
  id,
  refreshKey = 0,
  focusIds = [],
  previewId = null,
  onPick,
  onBlocksLoaded,
}: {
  id: string;
  refreshKey?: number;
  /** Ids currently selected; outlined in the preview. */
  focusIds?: string[];
  /**
   * The @-menu's active item: highlighted and scrolled into view without
   * being selected, so filtering the menu shows you what you'd be picking.
   */
  previewId?: string | null;
  /** A block was clicked (null when the user clicked empty space). */
  onPick?: (block: FocusBlock | null, additive: boolean) => void;
  /**
   * Every pickable item present after a (re)load, in document order. Feeds
   * both stale-pick pruning and the composer's @-menu — the rendered page is
   * the only place that knows what's actually on screen and what it says.
   */
  onBlocksLoaded?: (items: FocusBlock[]) => void;
}) {
  const frameRef = useRef<HTMLIFrameElement>(null);
  // Kept in refs so the load handler never needs re-binding when the parent
  // re-renders with fresh callback identities.
  const onPickRef = useRef(onPick);
  onPickRef.current = onPick;
  const onBlocksLoadedRef = useRef(onBlocksLoaded);
  onBlocksLoadedRef.current = onBlocksLoaded;
  // Bumped on each iframe load so the outline effect re-runs against the new
  // document — the previous one's elements are gone.
  const [loadCount, setLoadCount] = useState(0);

  const handleLoad = useCallback(() => {
    const doc = frameRef.current?.contentDocument;
    if (!doc) return;

    if (!doc.getElementById(FOCUS_STYLE_ID)) {
      const style = doc.createElement("style");
      style.id = FOCUS_STYLE_ID;
      style.textContent = FOCUS_CSS;
      doc.head.appendChild(style);
    }

    doc.addEventListener("click", (e: MouseEvent) => {
      const el = blockAt(doc, e.clientX, e.clientY);
      if (!el) {
        onPickRef.current?.(null, false);
        return;
      }
      // Links carry target=_blank; navigating away mid-edit would be a
      // surprise. Plain and modified left-clicks both pick — middle-click is
      // left alone so "open in new tab" still works for checking a link.
      e.preventDefault();
      const additive = e.metaKey || e.ctrlKey || e.shiftKey;
      // Shift-click also extends the browser's text selection, which paints a
      // highlight across the blocks being picked. The pick is the intent here.
      if (additive) doc.getSelection()?.removeAllRanges();
      onPickRef.current?.(toFocusBlock(el), additive);
    });

    onBlocksLoadedRef.current?.(
      Array.from(doc.querySelectorAll("[data-block-id]"))
        .filter((el) => el.getAttribute("data-block-id"))
        .map(toFocusBlock),
    );
    setLoadCount((n) => n + 1);
  }, []);

  useEffect(() => {
    const doc = frameRef.current?.contentDocument;
    if (!doc) return;
    const wanted = new Set(focusIds);
    doc.querySelectorAll("[data-block-id]").forEach((el) => {
      if (wanted.has(el.getAttribute("data-block-id") ?? "")) {
        el.setAttribute("data-mp-focused", "");
      } else {
        el.removeAttribute("data-mp-focused");
      }
    });
  }, [focusIds, loadCount]);

  // Live preview for the @-menu. Scrolling matters as much as the outline:
  // the item you're filtering towards is usually off screen, and a highlight
  // you can't see is no better than no highlight.
  useEffect(() => {
    const doc = frameRef.current?.contentDocument;
    if (!doc) return;
    doc
      .querySelectorAll("[data-mp-preview]")
      .forEach((el) => el.removeAttribute("data-mp-preview"));
    if (!previewId) return;
    const el = doc.querySelector(`[data-block-id="${CSS.escape(previewId)}"]`);
    if (!el) return;
    el.setAttribute("data-mp-preview", "");
    el.scrollIntoView({ block: "center", behavior: "smooth" });
  }, [previewId, loadCount]);

  return (
    <iframe
      ref={frameRef}
      onLoad={handleLoad}
      src={`/api/portals/${encodeURIComponent(id)}/preview?v=${refreshKey}`}
      title="Portal preview"
      style={{
        width: "100%",
        height: "100%",
        border: "none",
        display: "block",
      }}
    />
  );
}
